import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import { useNavigate } from 'react-router-dom'
import { StatusChip } from '@/shared/components/StatusChip'
import { useAppWorkspaceStore } from './appWorkspaceStore'
import { confirmCloseWorkspaceTab } from './confirmCloseWorkspaceTab'
import { chromeTabBarSx } from './chromeTabBarSx'

export function AppTabBar() {
  const navigate = useNavigate()
  const tabs = useAppWorkspaceStore((s) => s.tabs)
  const activePath = useAppWorkspaceStore((s) => s.activePath)

  const visible = tabs.filter((t) => t.presentation === 'tab')
  if (visible.length === 0) return null

  const value = visible.some((t) => t.path === activePath) ? activePath : false

  const handleClose = async (path: string) => {
    const next = await confirmCloseWorkspaceTab(path)
    if (next == null) return
    navigate(next)
  }

  return (
    <Box
      data-testid="app-tab-bar"
      data-tab-style="chrome"
      sx={[
        {
          display: 'flex',
          alignItems: 'flex-end',
          flexShrink: 0,
          minHeight: 36,
          gap: 1,
        },
        chromeTabBarSx,
      ]}
    >
      <Tabs
        value={value}
        onChange={(_, v: string) => navigate(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 32,
        }}
      >
        {visible.map((tab) => (
          <Tab
            key={tab.surfaceKey}
            value={tab.path}
            onMouseDown={(e) => {
              if (e.button === 1) {
                e.preventDefault()
                handleClose(tab.path)
              }
            }}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{tab.title}</span>
                {tab.status ? (
                  <Box component="span" sx={{ pointerEvents: 'none', display: 'inline-flex' }}>
                    <StatusChip status={tab.status} label={tab.status} />
                  </Box>
                ) : null}
                {tab.isDirty && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      // `palette.treeNode` is a DESIGNER-theme token and does not
                      // exist in the App themes — reading it crashed the strip the
                      // moment a tab first went dirty. The Designer sets that token
                      // to warning.main anyway, and the App already marks required
                      // fields with it, so the dot stays visually identical.
                      bgcolor: 'warning.main',
                    }}
                  />
                )}
                {tab.kind === 'record' && (
                  <IconButton
                    component="div"
                    size="small"
                    aria-label="Open as window"
                    sx={{ p: 0, color: 'text.secondary' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      useAppWorkspaceStore.getState().setPresentation(tab.path, 'modal')
                    }}
                  >
                    <CloseFullscreenIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  component="div"
                  size="small"
                  aria-label={`Close ${tab.title}`}
                  sx={{ p: 0, ml: 0.25 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleClose(tab.path)
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          />
        ))}
      </Tabs>
    </Box>
  )
}
